import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { useSelector } from 'react-redux';
import {
  selectEmployeeData,
  selectSwapShiftsReportsData,
} from '../../../store/ReportsDetailsSlice/selectors';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  clearPtoReportData,
  getEmployeeList,
  getSwapShiftsReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import moment from 'moment';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import ReusableGrid from '../../../components/Grid';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const FORMAT_DATE_STRING = 'YYYY-MM-DD';
const HEADERS = [
  {
    headerName: 'Shift',
    field: 'shift',
  },
  {
    headerName: '',
    field: 'fill',
  },
  {
    headerName: '',
    field: 'shift-type',
  },
  {
    headerName: 'Filled By',
    field: 'filled-by',
  },
  {
    headerName: 'Hours',
    field: 'hours',
  },
  {
    headerName: 'Status',
    field: 'status',
  },
];

const createParams = (param) =>
  param && param.length > 0 ? param.map((par) => par.id) : ['ALL'];

const SwapShiftsReport = () => {
  const dispatch = useDispatch();

  const { register, setValue, watch } = useForm({
    defaultValues: {
      emp_code: [],
    },
  });

  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);
  const { swapShiftsData, isLoadingSwapShifts } = useSelector(
    selectSwapShiftsReportsData
  );
  const isTabletWidth = useMediaQuery('(max-width:899px)');

  const { fromDate, toDate, render } = useDateRangeFilter();

  // Get form values
  const { emp_code: employees } = watch();
  const empParam = useMemo(() => createParams(employees), [employees]);

  const reqParams = useMemo(
    () => ({
      start_dt: moment(fromDate).format(FORMAT_DATE_STRING) + ' 00:00:00',
      end_dt: moment(toDate).format(FORMAT_DATE_STRING) + ' 23:59:59',
      sel_emp:
        empParam.length === 1 && empParam[0] === 'ALL' ? undefined : empParam,
    }),
    [empParam, fromDate, toDate]
  );

  const handleFindClick = () => dispatch(getSwapShiftsReportData(reqParams));

  useEffect(
    () => {
      dispatch(getEmployeeList());
      dispatch(getSwapShiftsReportData(reqParams));
      return () => dispatch(clearPtoReportData());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const rawFilterConfig = [
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);
  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Shift Swaps Report Details" />

      <Box xs={12} className="d-flex justify-content-between">
        <Box sx={{ width: '100%', mr: 2 }}>
          <Grid container sx={{ width: '100%', mb: '24px' }}>
            <FilterPanel filters={filterConfig} columns={4} />
          </Grid>

          <Grid container sx={{ width: '100%' }}>
            <Grid
              item
              sx={{
                display: 'flex',
                gap: 2,
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Grid
                item
                sx={{
                  display: 'flex',
                  flexDirection: isTabletWidth ? 'column' : 'row',
                  gap: 2,
                  width: '100%',
                  mt: 'auto',
                }}
              >
                {render}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {isLoadingSwapShifts ? (
        <Box
          sx={{ minHeight: '30px' }}
          className="w-10 h-10 d-flex justify-content-center align-items-center"
        >
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          {swapShiftsData.length > 0 ? (
            <Box mt={4}>
              {swapShiftsData.map((item) => (
                <Box key={item.name} mb={4}>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    {item.name}
                  </Typography>
                  <ReusableGrid
                    columns={HEADERS}
                    rows={item.items}
                    footer={HEADERS.map((col) => {
                      if (col.field === 'hours') {
                        const total = item.items.reduce((sum, row) => {
                          const val = +row.hours;
                          return Number.isNaN(val) ? sum : sum + val;
                        }, 0);
                        return (+total.toFixed(2)).toString();
                      }
                      return '';
                    })}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <DataNotFound />
          )}
        </>
      )}
    </Paper>
  );
};

export default SwapShiftsReport;
